import { useEffect, useMemo, useState } from 'react';
import type { WizardAnswers, WizardConfig, WizardCondition, WizardQuestion } from '../types/wizard';
import type { JsonSchema } from '../types/schema';
import { generateGameSchemaFromWizard } from '../utils/gameSchemaWizard';

type GameSchemaWizardProps = {
  config: WizardConfig;
  onCancel: () => void;
  onComplete: (schema: JsonSchema, answers: WizardAnswers) => void;
};

const getAnswerValue = (answers: WizardAnswers, path: string): unknown => {
  if (Object.prototype.hasOwnProperty.call(answers, path)) {
    return answers[path];
  }
  const segments = path.split('.');
  let current: unknown = answers;
  for (const segment of segments) {
    if (!current || typeof current !== 'object' || !(segment in (current as Record<string, unknown>))) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
};

const evaluateCondition = (condition: WizardCondition | undefined, answers: WizardAnswers): boolean => {
  if (!condition) {
    return true;
  }
  switch (condition.fn) {
    case 'equals': {
      const [path, expected] = condition.args;
      return getAnswerValue(answers, path) === expected;
    }
    case 'includes': {
      const [path, value] = condition.args;
      const current = getAnswerValue(answers, path);
      return Array.isArray(current) && current.includes(value);
    }
    case 'and':
      return condition.args.every((entry) => evaluateCondition(entry, answers));
    case 'or':
      return condition.args.some((entry) => evaluateCondition(entry, answers));
    case 'not':
      return !evaluateCondition(condition.args[0], answers);
    default:
      return true;
  }
};

const buildDefaultValue = (question: WizardQuestion): unknown => {
  if (question.default !== undefined) {
    return question.default;
  }
  switch (question.type) {
    case 'checkbox':
      return false;
    case 'multiSelect':
    case 'tags':
      return [];
    case 'integer':
    case 'number':
      return null;
    case 'repeatGroup':
      return [];
    case 'textarea':
    case 'text':
    case 'singleSelect':
    default:
      return '';
  }
};

const buildRepeatDefaults = (questions: WizardQuestion[] = []) => {
  const defaults: Record<string, unknown> = {};
  questions.forEach((question) => {
    if (question.type === 'group') {
      Object.assign(defaults, buildRepeatDefaults(question.questions));
      return;
    }
    if (question.type === 'repeatGroup') {
      defaults[question.id] = [];
      return;
    }
    if (question.type === 'info') {
      return;
    }
    defaults[question.id] = buildDefaultValue(question);
  });
  return defaults;
};

const initializeAnswers = (config: WizardConfig): WizardAnswers => {
  const answers: WizardAnswers = {};
  const seedQuestions = (questions: WizardQuestion[]) => {
    questions.forEach((question) => {
      if (question.type === 'group') {
        seedQuestions(question.questions ?? []);
        return;
      }
      if (question.type === 'repeatGroup') {
        const minItems = question.minItems ?? (question.required ? 1 : 0);
        const items: Record<string, unknown>[] = [];
        for (let i = 0; i < minItems; i += 1) {
          items.push(buildRepeatDefaults(question.questions));
        }
        answers[question.id] = items;
        return;
      }
      if (question.type === 'info') {
        return;
      }
      answers[question.id] = buildDefaultValue(question);
    });
  };

  config.steps.forEach((step) => seedQuestions(step.questions));
  return answers;
};

const isAnswerPresent = (value: unknown) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};

const isQuestionComplete = (question: WizardQuestion, answers: WizardAnswers): boolean => {
  if (!evaluateCondition(question.visibleIf, answers)) {
    return true;
  }

  if (question.type === 'group') {
    return (question.questions ?? []).every((sub) => isQuestionComplete(sub, answers));
  }

  if (question.type === 'repeatGroup') {
    const items = Array.isArray(answers[question.id]) ? (answers[question.id] as Array<Record<string, unknown>>) : [];
    const minItems = question.minItems ?? (question.required ? 1 : 0);
    if (items.length < minItems) {
      return false;
    }
    return items.every((item) => {
      return (question.questions ?? []).every((sub) => {
        if (!evaluateCondition(sub.visibleIf, answers)) {
          return true;
        }
        if (!sub.required) {
          return true;
        }
        const value = item[sub.id];
        return isAnswerPresent(value);
      });
    });
  }

  if (!question.required) {
    return true;
  }

  if (question.type === 'info') {
    return true;
  }

  return isAnswerPresent(answers[question.id]);
};

export const GameSchemaWizard = ({ config, onCancel, onComplete }: GameSchemaWizardProps) => {
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<WizardAnswers>(() => initializeAnswers(config));
  const [tagDrafts, setTagDrafts] = useState<Record<string, string>>({});

  const visibleSteps = useMemo(
    () => config.steps.filter((step) => evaluateCondition(step.visibleIf, answers)),
    [config, answers]
  );

  useEffect(() => {
    if (stepIndex < visibleSteps.length) {
      return;
    }
    setStepIndex(Math.max(0, visibleSteps.length - 1));
  }, [stepIndex, visibleSteps.length]);

  const currentStep = visibleSteps[stepIndex];
  const isLastStep = stepIndex === visibleSteps.length - 1;
  const canProceed = currentStep
    ? currentStep.questions.every((question) => isQuestionComplete(question, answers))
    : false;

  const updateAnswer = (id: string, value: unknown) => {
    setAnswers((prev) => {
      if (prev[id] === value) {
        return prev;
      }
      return { ...prev, [id]: value };
    });
  };

  const renderInput = (question: WizardQuestion) => {
    if (!evaluateCondition(question.visibleIf, answers)) {
      return null;
    }

    const label = question.label ?? question.id;

    if (question.type === 'info') {
      return (
        <div className="wizard-info" key={question.id}>
          <p className="muted">{label}</p>
        </div>
      );
    }

    if (question.type === 'group') {
      return (
        <div className="wizard-group" key={question.id}>
          {question.label && <div className="wizard-group-title">{label}</div>}
          {(question.questions ?? []).map((sub) => renderInput(sub))}
        </div>
      );
    }

    if (question.type === 'repeatGroup') {
      const items = Array.isArray(answers[question.id]) ? (answers[question.id] as Array<Record<string, unknown>>) : [];
      const minItems = question.minItems ?? (question.required ? 1 : 0);
      const canRemove = items.length > minItems;

      const updateRepeatItem = (index: number, subId: string, value: unknown) => {
        setAnswers((prev) => {
          const current = Array.isArray(prev[question.id])
            ? [...(prev[question.id] as Array<Record<string, unknown>>)]
            : [];
          const base = current[index] ?? {};
          const nextItem = { ...base, [subId]: value };
          current[index] = nextItem;
          return { ...prev, [question.id]: current };
        });
      };

      const addRepeatItem = () => {
        const template = buildRepeatDefaults(question.questions);
        setAnswers((prev) => {
          const current = Array.isArray(prev[question.id])
            ? [...(prev[question.id] as Array<Record<string, unknown>>)]
            : [];
          current.push(template);
          return { ...prev, [question.id]: current };
        });
      };

      const removeRepeatItem = (index: number) => {
        setAnswers((prev) => {
          const current = Array.isArray(prev[question.id])
            ? [...(prev[question.id] as Array<Record<string, unknown>>)]
            : [];
          current.splice(index, 1);
          return { ...prev, [question.id]: current };
        });
      };

      return (
        <div className="wizard-question" key={question.id}>
          <div className="label">{label}</div>
          <div className="wizard-repeat">
            {items.map((item, index) => (
              <div className="wizard-repeat-item" key={`${question.id}-${index}`}>
                <div className="wizard-repeat-fields">
                  {(question.questions ?? []).map((sub) => {
                    if (!evaluateCondition(sub.visibleIf, answers)) {
                      return null;
                    }
                    const subValue = item[sub.id];
                    return (
                      <label className="wizard-field" key={`${question.id}-${index}-${sub.id}`}>
                        <span className="label">{sub.label ?? sub.id}</span>
                        {sub.type === 'singleSelect' ? (
                          <select
                            className="input"
                            value={typeof subValue === 'string' ? subValue : ''}
                            onChange={(event) => updateRepeatItem(index, sub.id, event.target.value)}
                          >
                            <option value="">Select...</option>
                            {(sub.options ?? []).map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : sub.type === 'checkbox' ? (
                          <input
                            type="checkbox"
                            checked={subValue === true}
                            onChange={(event) => updateRepeatItem(index, sub.id, event.target.checked)}
                          />
                        ) : sub.type === 'integer' || sub.type === 'number' ? (
                          <input
                            className="input"
                            type="number"
                            step={sub.type === 'integer' ? 1 : 'any'}
                            value={typeof subValue === 'number' && Number.isFinite(subValue) ? subValue : ''}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              if (nextValue === '') {
                                updateRepeatItem(index, sub.id, null);
                                return;
                              }
                              const parsed =
                                sub.type === 'integer'
                                  ? Number.parseInt(nextValue, 10)
                                  : Number.parseFloat(nextValue);
                              updateRepeatItem(index, sub.id, Number.isNaN(parsed) ? null : parsed);
                            }}
                          />
                        ) : (
                          <input
                            className="input"
                            type="text"
                            value={typeof subValue === 'string' ? subValue : ''}
                            onChange={(event) => updateRepeatItem(index, sub.id, event.target.value)}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="button danger"
                  onClick={() => removeRepeatItem(index)}
                  disabled={!canRemove}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="button" onClick={addRepeatItem}>
              Add item
            </button>
          </div>
        </div>
      );
    }

    if (question.type === 'multiSelect') {
      const current = Array.isArray(answers[question.id]) ? (answers[question.id] as string[]) : [];
      return (
        <div className="wizard-question" key={question.id}>
          <div className="label">{label}</div>
          <div className="wizard-options">
            {(question.options ?? []).map((option) => (
              <label key={option} className="checkline">
                <input
                  type="checkbox"
                  checked={current.includes(option)}
                  onChange={() => {
                    const next = current.includes(option)
                      ? current.filter((item) => item !== option)
                      : [...current, option];
                    updateAnswer(question.id, next);
                  }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (question.type === 'tags') {
      const current = Array.isArray(answers[question.id]) ? (answers[question.id] as string[]) : [];
      const draft = tagDrafts[question.id] ?? '';
      return (
        <div className="wizard-question" key={question.id}>
          <div className="label">{label}</div>
          <div className="wizard-tags">
            <div className="wizard-tag-input">
              <input
                className="input"
                type="text"
                value={draft}
                placeholder="Add tag"
                onChange={(event) =>
                  setTagDrafts((prev) => ({ ...prev, [question.id]: event.target.value }))
                }
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') {
                    return;
                  }
                  event.preventDefault();
                  const next = draft.trim();
                  if (!next) {
                    return;
                  }
                  updateAnswer(question.id, Array.from(new Set([...current, next])));
                  setTagDrafts((prev) => ({ ...prev, [question.id]: '' }));
                }}
              />
              <button
                type="button"
                className="button"
                onClick={() => {
                  const next = draft.trim();
                  if (!next) {
                    return;
                  }
                  updateAnswer(question.id, Array.from(new Set([...current, next])));
                  setTagDrafts((prev) => ({ ...prev, [question.id]: '' }));
                }}
              >
                Add
              </button>
            </div>
            {current.length > 0 && (
              <div className="wizard-tag-list">
                {current.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="button ghost"
                    onClick={() => updateAnswer(question.id, current.filter((item) => item !== tag))}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (question.type === 'checkbox') {
      const checked = answers[question.id] === true;
      return (
        <label className="wizard-question toggle" key={question.id}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => updateAnswer(question.id, event.target.checked)}
          />
          <span>{label}</span>
        </label>
      );
    }

    if (question.type === 'singleSelect') {
      const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
      return (
        <label className="wizard-question" key={question.id}>
          <span className="label">{label}</span>
          <select className="input" value={value} onChange={(event) => updateAnswer(question.id, event.target.value)}>
            <option value="">Select...</option>
            {(question.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (question.type === 'textarea') {
      const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
      return (
        <label className="wizard-question" key={question.id}>
          <span className="label">{label}</span>
          <textarea
            className="input"
            rows={4}
            value={value}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
          />
        </label>
      );
    }

    if (question.type === 'integer' || question.type === 'number') {
      const value = answers[question.id];
      const inputValue = typeof value === 'number' && Number.isFinite(value) ? value : '';
      return (
        <label className="wizard-question" key={question.id}>
          <span className="label">{label}</span>
          <input
            className="input"
            type="number"
            step={question.type === 'integer' ? 1 : 'any'}
            value={inputValue}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (nextValue === '') {
                updateAnswer(question.id, null);
                return;
              }
              const parsed =
                question.type === 'integer' ? Number.parseInt(nextValue, 10) : Number.parseFloat(nextValue);
              updateAnswer(question.id, Number.isNaN(parsed) ? null : parsed);
            }}
          />
        </label>
      );
    }

    const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
    return (
      <label className="wizard-question" key={question.id}>
        <span className="label">{label}</span>
        <input className="input" type="text" value={value} onChange={(event) => updateAnswer(question.id, event.target.value)} />
      </label>
    );
  };

  if (!started) {
    return (
      <section className="panel wizard-panel">
        <div className="wizard-intro">
          <div className="wizard-intro-text">
            <h2>{config.title}</h2>
            <p>
              Creating data models from scratch is hard and slow. This wizard will draft a clean baseline schema with
              the systems you need so you can refine it in minutes.
            </p>
          </div>
          <div className="wizard-actions">
            <button type="button" className="button primary" onClick={() => setStarted(true)}>
              Start wizard
            </button>
            <button type="button" className="button ghost" onClick={onCancel}>
              Continue manually
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!currentStep) {
    return (
      <section className="panel wizard-panel">
        <h2>No wizard steps available.</h2>
        <div className="wizard-actions">
          <button type="button" className="button ghost" onClick={onCancel}>
            Back to editor
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel wizard-panel">
      <div className="wizard-header">
        <div>
          <div className="panel-title">{config.title}</div>
          <div className="muted">
            Step {stepIndex + 1} of {visibleSteps.length}
          </div>
        </div>
        <div className="wizard-stepper">
          {visibleSteps.map((step, index) => (
            <div key={step.id} className={`wizard-step ${index === stepIndex ? 'active' : ''}`}>
              <span>{step.title}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="wizard-body">
        <h3>{currentStep.title}</h3>
        <div className="wizard-questions">
          {currentStep.questions.map((question) => renderInput(question))}
        </div>
      </div>
      <div className="wizard-actions">
        <button type="button" className="button ghost" onClick={onCancel}>
          Cancel wizard
        </button>
        <div className="wizard-actions-spacer" />
        {stepIndex > 0 && (
          <button type="button" className="button" onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))}>
            Back
          </button>
        )}
        <button
          type="button"
          className="button primary"
          disabled={!canProceed}
          onClick={() => {
            if (!canProceed) {
              return;
            }
            if (isLastStep) {
              const schema = generateGameSchemaFromWizard(answers, config);
              onComplete(schema, answers);
            } else {
              setStepIndex((prev) => Math.min(visibleSteps.length - 1, prev + 1));
            }
          }}
        >
          {isLastStep ? 'Generate schema' : 'Next'}
        </button>
      </div>
    </section>
  );
};
